/**
 * Builds the `modal_opened` / `modal_closed` pair.
 *
 * Both are fired by `useModalAnalytics`, which every `Common/Modal` and
 * `Common/Dialog` calls — no feature emits these directly. The route scope
 * arrives already resolved because these builders stay pure and testable; the
 * hook is what reads it off the router.
 */

import { getSessionId } from "./session";
import type { ModalAnalyticsId } from "./modalIds";
import type {
  ModalClosedEvent,
  ModalCloseVia,
  ModalOpenedEvent,
  ModalOutcome,
  ModalSurface,
} from "./types";

interface ModalScope {
  hubId: number | null;
  projectId: number | null;
}

interface ModalOpenedInput extends ModalScope {
  analyticsId: ModalAnalyticsId;
  surface: ModalSurface;
}

interface ModalClosedInput extends ModalOpenedInput {
  outcome: ModalOutcome;
  via: ModalCloseVia;
  msOpen: number;
}

export const buildModalOpenedEvent = ({
  analyticsId,
  surface,
  hubId,
  projectId,
}: ModalOpenedInput): ModalOpenedEvent => ({
  name: "modal_opened",
  occurred_at: new Date().toISOString(),
  session_id: getSessionId(),
  hub_id: hubId,
  project_id: projectId,
  properties: { analytics_id: analyticsId, surface },
});

export const buildModalClosedEvent = ({
  analyticsId,
  surface,
  outcome,
  via,
  msOpen,
  hubId,
  projectId,
}: ModalClosedInput): ModalClosedEvent => ({
  name: "modal_closed",
  occurred_at: new Date().toISOString(),
  session_id: getSessionId(),
  hub_id: hubId,
  project_id: projectId,
  properties: {
    analytics_id: analyticsId,
    surface,
    outcome,
    via,
    // Rounded: the sub-millisecond precision performance.now() offers says
    // nothing about how long a human looked at a dialog, and a whole number
    // keeps the JSON small.
    ms_open: Math.round(msOpen),
  },
});
